import React from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Formik, FormikHelpers, Form } from 'formik';
import * as Yup from 'yup';

import { BackButton, FormGroupControl, FormGroupSelect } from '@components/Base';
import { userClaimUpdate } from './action';
import { useRouteParam } from '@hooks/params';
import { ClaimStatuses } from '@models/Claim';
import { useInitialValues } from './hook';
import i18n from '@components/i18n';

export type FormValues = {
  status: ClaimStatuses;
  note: string;
  totalClaimedAmount: number;
  approvedAmount?: number;
};

const groupProps = { className: 'mb-3' };

const validationSchema = Yup.object({
  status: Yup.string().required(i18n.t('common:required')),
  note: Yup.string().required(i18n.t('common:required')),
  totalClaimedAmount: Yup.number().required(),
  approvedAmount: Yup.number()
    .when('status', {
      is: (status: ClaimStatuses) => status === ClaimStatuses.Closed,
      then: Yup.number().required(i18n.t('common:required')),
      otherwise: Yup.number().notRequired(),
    })
    .max(Yup.ref('totalClaimedAmount')),
});

const ClaimEdit = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const claimableType = useRouteParam('claimableType');
  const claimableId = useRouteParam('claimableId');
  const initialValues = useInitialValues() as FormValues;

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    dispatch(userClaimUpdate({ ...values, claimableType, claimableId }));
    setSubmitting(false);
  };

  return (
    <>
      <BackButton className="mb-4" />
      <Card>
        <Card.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid }) => (
              <Form>
                <FormGroupControl
                  name="totalClaimedAmount"
                  label={t('claimForm.totalClaimedAmount.label')}
                  groupProps={groupProps}
                  type="number"
                />

                <FormGroupControl
                  name="approvedAmount"
                  label={t('claimForm.approvedAmount.label')}
                  groupProps={groupProps}
                  type="number"
                />

                <FormGroupSelect name="status" label={t('common:status')} groupProps={groupProps}>
                  <option value={ClaimStatuses.Open}>{t('claimStatuses.open')}</option>
                  <option value={ClaimStatuses.InReview}>{t('claimStatuses.inReview')}</option>
                  <option value={ClaimStatuses.InProgress}>{t('claimStatuses.inProgress')}</option>
                  <option value={ClaimStatuses.Approved}>{t('claimStatuses.approved')}</option>
                  <option value={ClaimStatuses.SettlementOffered}>{t('claimStatuses.settlementOffered')}</option>
                  <option value={ClaimStatuses.PaymentProcessing}>{t('claimStatuses.paymentProcessing')}</option>
                  <option value={ClaimStatuses.Closed}>{t('claimStatuses.closed')}</option>
                  <option value={ClaimStatuses.Voided}>{t('claimStatuses.voided')}</option>
                </FormGroupSelect>

                <FormGroupControl as="textarea" name="note" label={t('common:note')} groupProps={groupProps} />

                <Button type="submit" disabled={isSubmitting || !isValid}>
                  {t('common:submit')}
                </Button>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </>
  );
};

export { ClaimEdit };
